// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.

import React, { Component } from "react";
import {
  TextField, Dropdown, DefaultButton, Icon, IconButton,
  FocusZone, FocusZoneTabbableElements, Checkbox
} from "office-ui-fabric-react";
import { withTranslation } from "react-i18next";

import { print } from "../../services/LoggingService";
import { eventService } from "../../services/EventService";
import { settingsService } from "../../services/SettingsService";

import "./QueryComponent.scss";
import { SaveQueryDialogComponent } from "./SaveQueryDialogComponent/SaveQueryDialogComponent";
import { ConfirmQueryDialogComponent } from "./ConfirmQueryDialogComponent/ConfirmQueryDialogComponent";
import MsalAuthService from "../../services/MsalAuthService";
import ShareQueryButton from "../ShareQueryButton/ShareQueryButton";
import TelemetryService from "../../services/TelemetryService";
import { AppRegion, ComponentName, TelemetryEvents, TelemetryTrigger } from "../../services/models/Constants/TelemetryConstants";
import { TelemetryEvent } from "../../services/models/Telemetry";

const defaultQuery = "SELECT * FROM digitaltwins";

const ENTER_KEY_CODE = 13;
class QueryComponent extends Component {

  queryOptions = [
    { key: "query", text: "Query" },
    { key: "save", text: "Save Query" }
  ]

  constructor(props) {
    super(props);
    this.state = {
      queries: [],
      selectedQuery: defaultQuery,
      selectedQueryMultiline: defaultQuery,
      selectedQueryKey: null,
      queryKeyToBeRemoved: "",
      showSaveQueryModal: false,
      showConfirmDeleteModal: false,
      showConfirmOverwriteModal: false,
      newQueryName: "",
      isOverlayResultsChecked: false,
      multiline: false,
      multilineHolder: false,
      isFocused: false,
      disabled: false,
      rowCount: 2
    };
  }

  componentDidMount() {
    this.setState({ queries: settingsService.queries });
    eventService.subscribeEnvironmentChange(this.clearAfterEnvironmentChange);

    // Immediately fire query if present as a URL parameter
    const urlParamQuery = MsalAuthService.getUrlParam("query");
    if (urlParamQuery) {
      const sanitizedQuery = this.sanitizeQuery(urlParamQuery);
      this.setState({ selectedQuery: sanitizedQuery });
      setTimeout(this.executeQuery);
    }
  }

  sanitizeQuery = (query) => {
    if (!query) return "";

    let sanitized = query;

    // Defense in depth: Multiple passes to handle nested/obfuscated attacks

    // Pass 1: Remove dangerous protocols (before they can be interpreted)
    sanitized = sanitized.replace(/javascript:/gi, '');
    sanitized = sanitized.replace(/data:/gi, '');
    sanitized = sanitized.replace(/vbscript:/gi, '');
    sanitized = sanitized.replace(/file:/gi, '');

    // Pass 2: Remove event handlers (before tag removal to catch edge cases)
    sanitized = sanitized.replace(/\bon\w+\s*=/gi, '');

    // Pass 3: Iteratively remove HTML/XML tags (handles nested tags like <<script>script>)
    let previous;
    let maxIterations = 10; // Prevent infinite loops in case of unexpected input
    let iterations = 0;
    do {
      previous = sanitized;
      // Remove any HTML/XML tags
      sanitized = sanitized.replace(/<[^>]*>/g, '');
      iterations++;
    } while (sanitized !== previous && iterations < maxIterations);

    // Pass 4: Remove remaining script/iframe artifacts that might have been exposed
    sanitized = sanitized.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
    sanitized = sanitized.replace(/<\/script>/gi, '');
    sanitized = sanitized.replace(/<\/?iframe[^>]*>/gi, '');

    // Pass 5: Remove dangerous HTML entities that could be decoded later
    sanitized = sanitized.replace(/&lt;/gi, '');
    sanitized = sanitized.replace(/&gt;/gi, '');
    sanitized = sanitized.replace(/&#60;/gi, '');
    sanitized = sanitized.replace(/&#62;/gi, '');
    sanitized = sanitized.replace(/&#x3C;/gi, '');
    sanitized = sanitized.replace(/&#x3E;/gi, '');

    // Pass 6: Remove null bytes and other control characters that could be used for obfuscation
    // eslint-disable-next-line no-control-regex
    sanitized = sanitized.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F]/g, '');

    // Pass 7: Limit length to prevent DoS
    const MAX_QUERY_LENGTH = 10000;
    if (sanitized.length > MAX_QUERY_LENGTH) {
      sanitized = sanitized.substring(0, MAX_QUERY_LENGTH);
    }

    return sanitized.trim();
  }

  onKeyFunction = event => {
    this.setState({ disabled: false });
    const enterPressed = event.key === "Enter";
    if (event.shiftKey && enterPressed) {
      if (!this.state.multilineHolder) {
        this.setState({ selectedQueryMultiline: event.target.value });
      }
      this.setState({ multiline: true, multilineHolder: true });
      this.queryField.focus();
    } else if (enterPressed) {
      this.executeQuery(event);
      event.preventDefault();
    }
  }

  componentWillUnmount() {
    eventService.unsubscribeEnvironmentChange(this.clearAfterEnvironmentChange);
  }

  clearAfterEnvironmentChange = () => {
    this.setState({
      selectedQuery: defaultQuery,
      selectedQueryKey: null,
      queryKeyToBeRemoved: "",
      showSaveQueryModal: false,
      showConfirmDeleteModal: false,
      showConfirmOverwriteModal: false,
      newQueryName: "",
      isOverlayResultsChecked: false
    });
  }

  onChange = evt => {
    this.setState({ selectedQuery: evt.target.value, selectedQueryKey: null, selectedQueryMultiline: evt.target.value });
    const count = evt.target.value.split("").filter(c => c === "\n").length;
    this.setState({ rowCount: count + 1, multilineHolder: count !== 0, multiline: count !== 0 });
  }

  onFocusGained = () => {
    if (this.state.multilineHolder) {
      this.setState(prevState => ({ selectedQuery: prevState.selectedQueryMultiline, multiline: true }));
    }
  }

  onFocusLost = () => {
    this.setState(prevState => ({ disabled: true, multiline: false, selectedQuery: prevState.selectedQueryMultiline.replaceAll("\n", " ") }));
  }

  onMouseOver = () => {
    this.setState({ disabled: false });
  }

  onChangeQueryName = evt => {
    this.setState({ newQueryName: evt.target.value });
  }

  executeQuery = event => {
    if (typeof this.props.onQueryExecuted === "function") {
      this.props.onQueryExecuted();
    }
    event?.preventDefault();
    print(`Requested query: ${this.state.selectedQuery}`);
    eventService.publishQuery(this.state.selectedQuery);
    // Log execute query
    const telemetryEvent = TelemetryEvents.QueryExplorer.QueryComponent.UserAction.ExecuteQuery;
    TelemetryService.sendEvent(
      new TelemetryEvent({
        triggerType: TelemetryTrigger.UserAction,
        componentName: ComponentName.Query,
        appRegion: AppRegion.QueryCommandBar,
        name: telemetryEvent.eventName,
        customProperties: {
          [telemetryEvent.properties.isOverlayEnabled]: this.state.isOverlayResultsChecked
        }
      })
    );
  }

  saveQueryButtonClicked = () => {
    this.setState({ showSaveQueryModal: true });
  }

  saveQuery = e => {
    e.preventDefault();
    const { queries, selectedQuery, newQueryName } = this.state;
    if (newQueryName) {
      const newQueries = [...queries];
      if (queries.find(q => q.name === newQueryName)) {
        this.setState({ showSaveQueryModal: false, showConfirmOverwriteModal: true });
      } else {
        newQueries.push({ name: newQueryName, query: selectedQuery });
        this.setState({ queries: newQueries, selectedQueryKey: newQueryName, showSaveQueryModal: false, newQueryName: "" });
        settingsService.queries = newQueries;
      }
      TelemetryService.sendEvent(
        new TelemetryEvent({
          triggerType: TelemetryTrigger.UserAction,
          componentName: ComponentName.Query,
          appRegion: AppRegion.QueryCommandBar,
          name: TelemetryEvents.QueryExplorer.QueryComponent.UserAction.ClickSaveQuery.eventName
        })
      );
    }
  }

  overwriteQuery = e => {
    e.preventDefault();
    const { queries, selectedQuery, newQueryName } = this.state;
    if (newQueryName) {
      const newQueries = [...queries];
      newQueries[newQueries.indexOf(newQueries.find(q => q.name === newQueryName))] = { name: newQueryName, query: selectedQuery };
      this.setState({ queries: newQueries, selectedQueryKey: newQueryName, showConfirmOverwriteModal: false, newQueryName: "" });
      settingsService.queries = newQueries;
    }
  }

  cancelSaveQuery = e => {
    e.preventDefault();
    this.setState({ showSaveQueryModal: false, newQueryName: "", showConfirmOverwriteModal: false });
  }

  onSelectedQueryChange = (e, i) => {
    this.setState(prevState => ({
      selectedQuery: prevState.queries.find(q => q.name === i.key).query,
      selectedQueryKey: i.key, selectedQueryMultiline: prevState.queries.find(q => q.name === i.key).query, multilineHolder: false
    }));
  }

  confirmDeleteQuery = e => {
    e.preventDefault();
    this.removeQuery();
    this.setState({ showConfirmDeleteModal: false, queryKeyToBeRemoved: "" });
  }

  cancelDeleteQuery = e => {
    e.preventDefault();
    this.setState({ showConfirmDeleteModal: false, queryKeyToBeRemoved: "" });
  }

  onRemoveQueryClick = item => {
    this.setState({ showConfirmDeleteModal: true, queryKeyToBeRemoved: item.key });
  }

  removeQuery = () => {
    const { queries } = this.state;
    const newQueries = [...queries];
    newQueries.splice(newQueries.indexOf(newQueries.find(q => q.name === this.state.queryKeyToBeRemoved)), 1);

    this.setState({ queries: newQueries });
    settingsService.queries = newQueries;
  }

  onRenderOption = item => (
    <div className="dropdown-option">
      <span>{item.key}</span>
      <button tabIndex="0" type="button" onClick={() => this.onRemoveQueryClick(item)}>
        <Icon
          className="close-icon"
          iconName="ChromeClose"
          aria-hidden="true"
          aria-label={`Remove query ${item.key}`}
          role="button"
          title="Remove query"
          tabIndex="0" />
      </button>
    </div>)

  onOverlayResultsChange = (e, checked) => {
    this.setState({ isOverlayResultsChecked: !!checked });
    eventService.publishOverlayQueryResults(!!checked);
  };

  handleOverlayResultsKeyUp = e => {
    if (e.keyCode === ENTER_KEY_CODE) {
      eventService.publishOverlayQueryResults(!this.state.isOverlayResultsChecked);
      this.setState(prevState => ({ isOverlayResultsChecked: !prevState.isOverlayResultsChecked }));
    }
  }

  render() {
    const { queries, selectedQuery, selectedQueryKey, showSaveQueryModal, newQueryName,
      showConfirmDeleteModal, showConfirmOverwriteModal, isOverlayResultsChecked, disabled, multiline, rowCount, isFocused } = this.state;

    return (
      <>
        <div className="qc-grid">
          <div className="qc-queryBox">
            <div className="qc-label">
              <Dropdown
                placeholder={this.props.t("queryComponent.savedQueries")}
                ariaLabel={this.props.t("queryComponent.savedQueries")}
                selectedKey={selectedQueryKey}
                options={queries.map(q => ({ key: q.name, text: q.name }))}
                onRenderOption={this.onRenderOption}
                onKeyDown={this.onMouseOver}
                role="combobox"
                styles={{
                  dropdown: { width: 200 }
                }}
                onChange={this.onSelectedQueryChange} onFocus={this.onFocusOther} />
            </div>
            <FocusZone handleTabKey={FocusZoneTabbableElements.all} defaultActiveElement="#queryField">
              <form onSubmit={this.executeQuery}>
                <TextField id="queryField" className="qc-query" styles={this.getStyles} role="search" value={selectedQuery} onChange={this.onChange} ariaLabel="Enter a query"
                  onFocus={this.onFocusGained} multiline={multiline} rows={rowCount} autoAdjustHeight="true" isFocused={isFocused} onBlur={this.onFocusLost} disabled={disabled}
                  onMouseOver={this.onMouseOver} onKeyDown={this.onKeyFunction} ref={input => {
                    this.queryField = input;
                  }} />
              </form>
            </FocusZone>
            <div className="qc-queryControls">
              <FocusZone onKeyUp={this.handleOverlayResultsKeyUp} onKeyDown={this.onMouseOver} >
                <Checkbox label={this.props.t("queryComponent.overlayResults")} checked={isOverlayResultsChecked} onChange={this.onOverlayResultsChange}
                  boxSide="end" />
              </FocusZone>
              <DefaultButton className="query-button" onClick={this.executeQuery} ariaLive="assertive">
                {this.props.t("queryComponent.defaultButton")}
              </DefaultButton>
              <IconButton className="query-save-button"
                iconProps={{ iconName: this.props.t("queryComponent.iconButton"), style: { color: "black" } }}
                title={this.props.t("queryComponent.iconButton")} ariaLabel="Save query" ariaLive="assertive"
                onClick={this.saveQueryButtonClicked} />
              <ShareQueryButton query={selectedQuery} />
            </div>
          </div>
        </div>
        <SaveQueryDialogComponent isVisible={showSaveQueryModal}
          onConfirm={this.saveQuery} onCancel={this.cancelSaveQuery}
          onChange={this.onChangeQueryName} query={newQueryName} />
        <ConfirmQueryDialogComponent title={this.props.t("queryComponent.confirmQueryDialogComponent1.title")}
          description={this.props.t("queryComponent.confirmQueryDialogComponent1.description")}
          action="Confirm" isVisible={showConfirmOverwriteModal}
          onConfirm={this.overwriteQuery} onCancel={this.cancelSaveQuery}
          defaultActiveElementId="deleteQueryField" />
        <ConfirmQueryDialogComponent title={this.props.t("queryComponent.confirmQueryDialogComponent2.title")}
          action="Delete" isVisible={showConfirmDeleteModal}
          onConfirm={this.confirmDeleteQuery} onCancel={this.cancelDeleteQuery} />
      </>
    );
  }

}

export default withTranslation()(QueryComponent);
